// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	MarkdownBlock,
	markdownBlockProps,
} from '@app/features/messaging/components/markdown/renderers/common/MarkdownBlockAttributes';
import {
	getKatexModule,
	loadKatex,
	useLazyChunkLoad,
} from '@app/features/messaging/components/markdown/renderers/common/CodeElements';
import type {
	RendererProps,
} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import type {
	DisplayMathNode,
	InlineMathNode,
} from '@app/features/messaging/utils/markdown/parser/Nodes';
import {Logger} from '@app/features/platform/utils/AppLogger';
import codeElementsStyles from '@app/features/theme/styles/CodeElements.module.css';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import {CopyButton} from '@app/features/ui/components/CopyButton';
import {msg} from '@lingui/core/macro';
import React, {useMemo} from 'react';

const logger = new Logger('MathRenderer');
const COPY_LATEX_DESCRIPTOR = msg({
	message: 'Copy LaTeX',
	comment: 'Button tooltip for copying LaTeX math formula to clipboard',
});

export function InlineMathRenderer({node, id}: RendererProps<InlineMathNode>): React.ReactElement {
	const katex = getKatexModule();
	useLazyChunkLoad(loadKatex, katex !== null, true);

	const renderedHtml = useMemo(() => {
		if (!katex) return null;
		try {
			return katex.renderToString(node.content, {
				displayMode: false,
				throwOnError: false,
				errorColor: 'var(--accent-danger)',
				strict: false,
				trust: false,
				output: 'html',
			});
		} catch (error) {
			logger.warn('Inline KaTeX render failed:', error);
			return null;
		}
	}, [katex, node.content]);

	if (renderedHtml) {
		return (
			<span
				key={id}
				className={markupStyles.inlineLatex}
				dangerouslySetInnerHTML={{__html: renderedHtml}}
				data-flx="messaging.markdown.inline-math"
			/>
		);
	}

	return (
		<code
			key={id}
			className={markupStyles.inlineLatexPlaceholder}
			data-flx="messaging.markdown.inline-math.placeholder"
		>
			${node.content}$
		</code>
	);
}

export function DisplayMathRenderer({node, id}: RendererProps<DisplayMathNode>): React.ReactElement {
	const katex = getKatexModule();
	useLazyChunkLoad(loadKatex, katex !== null, true);

	const copyButton = (
		<CopyButton
			value={node.content}
			label={COPY_LATEX_DESCRIPTOR}
			className={markupStyles.codeActions}
			visibleClassName={markupStyles.codeActionsVisible}
			iconClassName={codeElementsStyles.icon}
			data-flx="messaging.markdown.display-math.copy-button"
		/>
	);

	const renderedHtml = useMemo(() => {
		if (!katex) return null;
		try {
			return katex.renderToString(node.content, {
				displayMode: true,
				throwOnError: false,
				errorColor: 'var(--accent-danger)',
				strict: false,
				trust: false,
				output: 'html',
			});
		} catch (error) {
			logger.warn('Display KaTeX render failed:', error);
			return null;
		}
	}, [katex, node.content]);

	if (renderedHtml) {
		return (
			<div
				key={id}
				className={markupStyles.latexCodeBlock}
				data-flx="messaging.markdown.display-math.block"
				{...markdownBlockProps(MarkdownBlock.LatexCode)}
			>
				<div className={markupStyles.codeContainer}>
					{copyButton}
					<div
						className={markupStyles.latexContent}
						dangerouslySetInnerHTML={{__html: renderedHtml}}
						data-flx="messaging.markdown.display-math.content"
					/>
				</div>
			</div>
		);
	}

	return (
		<div
			key={id}
			className={markupStyles.codeContainer}
			data-flx="messaging.markdown.display-math.placeholder"
			{...markdownBlockProps(MarkdownBlock.Code)}
		>
			{copyButton}
			<pre data-flx="messaging.markdown.display-math.pre">
				<code className={markupStyles.hljs}>$${node.content}$$</code>
			</pre>
		</div>
	);
}
